type Primitive = string | number | boolean;

export type TelemetryLevel = 'info' | 'warn' | 'error';
export type TelemetryConsent = 'granted' | 'denied';

export interface TelemetryEvent {
  name: string;
  level: TelemetryLevel;
  timestamp: number;
  message?: string;
  durationMs?: number;
  tags?: Record<string, string>;
  metrics?: Record<string, number>;
  context?: Record<string, Primitive>;
}

interface TelemetryBatch {
  app: string;
  version: string;
  sentAt: number;
  sessionId: string;
  sampleRate: number;
  userAgent: string;
  events: TelemetryEvent[];
}

interface TelemetryQueueItem {
  event: TelemetryEvent;
  enqueuedAt: number;
}

interface TelemetryConfig {
  enabled: boolean;
  endpoint: string;
  flushIntervalMs: number;
  maxQueueSize: number;
  maxBatchSize: number;
  sampleRate: number;
  debug: boolean;
}

const CONSENT_STORAGE_KEY = 'intouch-telemetry-consent-v1';
const SESSION_ID_STORAGE_KEY = 'intouch-telemetry-session-id-v1';
const DEFAULT_SAMPLE_RATE = import.meta.env.PROD ? 0.35 : 1;
const REDACT_KEY_PATTERN = /(password|pass|token|secret|auth|cookie|session|email|phone|ssn|credit|card|iban|jwt|key)/i;
const MAX_STRING_LENGTH = 280;
const MAX_CONTEXT_ENTRIES = 20;
const MAX_OBJECT_DEPTH = 3;
const EVENT_BUDGET_PER_MINUTE = 120;

let started = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let queue: TelemetryQueueItem[] = [];
let budgetWindowStartedAt = 0;
let budgetUsed = 0;
let sessionIdCache = '';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

function parseNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getDefaultEndpoint(): string {
  if (import.meta.env.VITE_TELEMETRY_ENDPOINT) {
    return String(import.meta.env.VITE_TELEMETRY_ENDPOINT);
  }

  if (import.meta.env.DEV) {
    return 'http://localhost:3001/api/telemetry/ingest';
  }

  return '';
}

function getConfig(): TelemetryConfig {
  const endpoint = getDefaultEndpoint();
  const hasEndpoint = endpoint.trim().length > 0;

  return {
    enabled: import.meta.env.VITE_TELEMETRY_ENABLED !== 'false' && hasEndpoint,
    endpoint,
    flushIntervalMs: clamp(parseNumber(import.meta.env.VITE_TELEMETRY_FLUSH_INTERVAL_MS, 12_000), 2_000, 60_000),
    maxQueueSize: clamp(parseNumber(import.meta.env.VITE_TELEMETRY_MAX_QUEUE_SIZE, 240), 20, 1000),
    maxBatchSize: clamp(parseNumber(import.meta.env.VITE_TELEMETRY_MAX_BATCH_SIZE, 30), 1, 200),
    sampleRate: clamp(parseNumber(import.meta.env.VITE_TELEMETRY_SAMPLE_RATE, DEFAULT_SAMPLE_RATE), 0, 1),
    debug: import.meta.env.DEV || import.meta.env.VITE_TELEMETRY_DEBUG === 'true',
  };
}

function getTelemetryConsent(): TelemetryConsent {
  if (!isBrowser()) return 'granted';

  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (raw === 'denied') return 'denied';
  return 'granted';
}

export function setTelemetryConsent(consent: TelemetryConsent): void {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  } catch {
    // Ignore local storage errors.
  }
}

export function telemetryIsEnabled(): boolean {
  if (!isBrowser()) return false;

  const config = getConfig();
  if (!config.enabled) return false;
  if (getTelemetryConsent() !== 'granted') return false;
  return true;
}

function getSessionId(): string {
  if (sessionIdCache) return sessionIdCache;

  if (!isBrowser()) {
    sessionIdCache = `session-${Date.now()}`;
    return sessionIdCache;
  }

  try {
    const stored = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (stored) {
      sessionIdCache = stored;
      return sessionIdCache;
    }
  } catch {
    // Ignore read errors.
  }

  const created = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sid-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  sessionIdCache = created;

  try {
    window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, created);
  } catch {
    // Ignore write errors.
  }

  return created;
}

function sanitizeString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (trimmed.length > MAX_STRING_LENGTH) {
    return `${trimmed.slice(0, MAX_STRING_LENGTH)}...`;
  }

  return trimmed;
}

function sanitizePrimitive(value: unknown): Primitive | undefined {
  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  return undefined;
}

function redactObject(input: unknown, depth: number = 0): Record<string, Primitive> {
  if (!input || typeof input !== 'object' || depth > MAX_OBJECT_DEPTH) {
    return {};
  }

  const entries = Object.entries(input as Record<string, unknown>);
  const limited = entries.slice(0, MAX_CONTEXT_ENTRIES);

  return limited.reduce<Record<string, Primitive>>((acc, [key, value]) => {
    if (REDACT_KEY_PATTERN.test(key)) {
      acc[key] = '[redacted]';
      return acc;
    }

    const primitive = sanitizePrimitive(value);
    if (primitive !== undefined) {
      acc[key] = primitive;
      return acc;
    }

    if (value && typeof value === 'object') {
      const nested = redactObject(value, depth + 1);
      const nestedKeys = Object.keys(nested);
      if (nestedKeys.length > 0) {
        acc[key] = `[object:${nestedKeys.length}]`;
      }
    }

    return acc;
  }, {});
}

function checkEventBudget(): boolean {
  const now = Date.now();
  const windowMs = 60_000;

  if (!budgetWindowStartedAt || now - budgetWindowStartedAt >= windowMs) {
    budgetWindowStartedAt = now;
    budgetUsed = 0;
  }

  if (budgetUsed >= EVENT_BUDGET_PER_MINUTE) {
    return false;
  }

  budgetUsed += 1;
  return true;
}

function shouldSampleEvent(sampleRate: number, name: string): boolean {
  if (sampleRate >= 1) return true;
  if (sampleRate <= 0) return false;

  const basis = `${getSessionId()}:${name}`;
  let hash = 0;

  for (let index = 0; index < basis.length; index += 1) {
    hash = (hash * 31 + basis.charCodeAt(index)) >>> 0;
  }

  const normalized = hash / 0xffffffff;
  return normalized <= sampleRate;
}

function normalizeTelemetryEvent(event: Partial<TelemetryEvent>): TelemetryEvent | null {
  if (!event.name || typeof event.name !== 'string') {
    return null;
  }

  const name = sanitizeString(event.name).slice(0, 120);
  if (!name) return null;

  const level: TelemetryLevel = event.level === 'warn' || event.level === 'error' ? event.level : 'info';
  const context = redactObject(event.context);
  const tagsRaw = event.tags || {};

  const tags = Object.entries(tagsRaw)
    .slice(0, 12)
    .reduce<Record<string, string>>((acc, [key, value]) => {
      if (!key) return acc;
      if (REDACT_KEY_PATTERN.test(key)) return acc;
      if (typeof value === 'string' && value.trim()) {
        acc[sanitizeString(key).slice(0, 60)] = sanitizeString(value).slice(0, 120);
      }
      return acc;
    }, {});

  const metricsRaw = event.metrics || {};
  const metrics = Object.entries(metricsRaw)
    .slice(0, 12)
    .reduce<Record<string, number>>((acc, [key, value]) => {
      if (!key || !Number.isFinite(value)) return acc;
      acc[sanitizeString(key).slice(0, 60)] = Number(value);
      return acc;
    }, {});

  const normalized: TelemetryEvent = {
    name,
    level,
    timestamp: Number.isFinite(event.timestamp) ? Number(event.timestamp) : Date.now(),
  };

  if (event.message && typeof event.message === 'string') {
    normalized.message = sanitizeString(event.message);
  }
  if (Number.isFinite(event.durationMs)) {
    normalized.durationMs = Math.max(0, Number(event.durationMs));
  }
  if (Object.keys(tags).length > 0) {
    normalized.tags = tags;
  }
  if (Object.keys(metrics).length > 0) {
    normalized.metrics = metrics;
  }
  if (Object.keys(context).length > 0) {
    normalized.context = context;
  }

  return normalized;
}

function createBatch(events: TelemetryEvent[], sampleRate: number): TelemetryBatch {
  return {
    app: 'InTouch',
    version: import.meta.env.VITE_APP_VERSION || '0.1.0',
    sentAt: Date.now(),
    sessionId: getSessionId(),
    sampleRate,
    userAgent: sanitizeString(navigator.userAgent || 'unknown'),
    events,
  };
}

function enqueue(event: TelemetryEvent): void {
  const config = getConfig();

  queue = [...queue, { event, enqueuedAt: Date.now() }].slice(-config.maxQueueSize);
}

function takeBatch(maxBatchSize: number): TelemetryQueueItem[] {
  if (queue.length === 0) return [];
  const batch = queue.slice(0, maxBatchSize);
  queue = queue.slice(batch.length);
  return batch;
}

function restoreBatch(items: TelemetryQueueItem[]): void {
  if (items.length === 0) return;
  const config = getConfig();
  queue = [...items, ...queue].slice(-config.maxQueueSize);
}

async function sendBatch(payload: TelemetryBatch, endpoint: string, debug: boolean): Promise<boolean> {
  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const accepted = navigator.sendBeacon(endpoint, blob);
      if (accepted) return true;
    } catch {
      // Fall through to fetch keepalive.
    }
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-InTouch-Telemetry': '1',
      },
      credentials: 'omit',
      keepalive: true,
      body,
    });

    return response.ok;
  } catch (error) {
    if (debug) {
      console.warn('[Telemetry] Failed to send batch', error);
    }
    return false;
  }
}

export async function flushTelemetryQueue(options?: { force?: boolean }): Promise<void> {
  if (!telemetryIsEnabled()) return;

  const config = getConfig();
  if (!config.endpoint) return;
  if (queue.length === 0) return;

  const items = takeBatch(config.maxBatchSize);
  if (items.length === 0) return;

  const payload = createBatch(items.map((item) => item.event), config.sampleRate);
  const success = await sendBatch(payload, config.endpoint, config.debug);

  if (!success && !options?.force) {
    restoreBatch(items);
  }
}

export function trackTelemetryEvent(event: Partial<TelemetryEvent>): void {
  if (!telemetryIsEnabled()) return;

  if (!checkEventBudget()) {
    return;
  }

  const config = getConfig();
  const normalized = normalizeTelemetryEvent(event);
  if (!normalized) return;

  const alwaysTrack = normalized.level === 'error';
  if (!alwaysTrack && !shouldSampleEvent(config.sampleRate, normalized.name)) {
    return;
  }

  enqueue(normalized);

  if (queue.length >= config.maxBatchSize) {
    void flushTelemetryQueue();
  }
}

export function trackTelemetryError(error: unknown, context?: Record<string, Primitive>): void {
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : 'Unknown error';

  const stackPreview = error instanceof Error && typeof error.stack === 'string'
    ? sanitizeString(error.stack).slice(0, MAX_STRING_LENGTH)
    : undefined;

  trackTelemetryEvent({
    name: 'client.error',
    level: 'error',
    message,
    context: {
      ...context,
      stack: stackPreview || 'n/a',
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    },
  });
}

function installLifecycleHooks(): void {
  if (!isBrowser()) return;

  const flushForShutdown = () => {
    void flushTelemetryQueue({ force: true });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushForShutdown();
    }
  });

  window.addEventListener('pagehide', flushForShutdown);
  window.addEventListener('online', () => {
    void flushTelemetryQueue();
  });
}

export function startTelemetryPipeline(): void {
  if (!telemetryIsEnabled()) return;
  if (started) return;

  started = true;
  installLifecycleHooks();

  const config = getConfig();

  flushTimer = setInterval(() => {
    void flushTelemetryQueue();
  }, config.flushIntervalMs);

  trackTelemetryEvent({
    name: 'telemetry.pipeline.started',
    level: 'info',
    context: {
      route: window.location.pathname,
      language: navigator.language,
      online: navigator.onLine,
    },
  });
}

export function stopTelemetryPipeline(): void {
  if (!started) return;
  started = false;

  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}
